import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DataPage.css';
import Pagination from '../components/Pagination';

import { API_URL } from '../config';
import { formatDate } from '../utils/formatDate';

function Pacientes() {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('ID');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [sexo, setSexo] = useState('');
  const [cidade, setCidade] = useState('');
  const [idadeMin, setIdadeMin] = useState('');
  const [idadeMax, setIdadeMax] = useState('');
  const [resumo, setResumo] = useState({
    total: 0,
    masculino: 0,
    feminino: 0,
    idadeMedia: 0,
    totalSolicitacoes: 0
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const navigate = useNavigate();

  // Initial fetch on mount
  useEffect(() => {
    fetchPacientes();
  }, []);

  // Auto-fetch on pagination/sort changes
  useEffect(() => {
    fetchPacientes();
  }, [currentPage, itemsPerPage, sortField, sortOrder]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPacientes();
  };

  const fetchPacientes = async (search = searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortField,
        sortOrder: sortOrder
      });

      if (search) params.append('search', search);
      if (sexo) params.append('sexo', sexo);
      if (cidade) params.append('cidade', cidade);
      if (idadeMin) params.append('idadeMin', idadeMin);
      if (idadeMax) params.append('idadeMax', idadeMax);

      const response = await axios.get(`${API_URL}/pacientes?${params.toString()}`);
      const { data, total } = response.data;

      setPacientes(data);
      setTotalItems(total);

      setResumo(response.data.totais || { total: 0, masculino: 0, feminino: 0, idadeMedia: 0, totalSolicitacoes: 0 });
    } catch (err) {
      console.error('Erro ao buscar pacientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (paciente) => {
    setSelectedPaciente(paciente);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPaciente(null);
  };

  const handleViewPaciente = (id) => {
    navigate(`/pacientes/${id}`);
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return null;
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Alterna a ordem se for o mesmo campo
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // Novo campo, começa com DESC
      setSortField(field);
      setSortOrder('DESC');
    }
  };

  // formatDate importado de ../utils/formatDate

  const styles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
    btnPesquisar: {
      padding: '8px 20px',
      backgroundColor: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '600',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <div className="data-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>👥</span>
          <div>
            <h1 style={styles.pageTitle}>Pacientes</h1>
            <p style={styles.pageSubtitle}>
              Exibindo {pacientes.length} de {totalItems} registros
              {searchTerm && ' (filtrado)'}
              {loading && <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}> ⟳</span>}
            </p>
          </div>
        </div>
      </div>

      <div className="search-section">
        <div className="search-form">
          <input
            type="text"
            placeholder="Buscar por nome, CPF, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            className="search-input"
          />
          <button style={styles.btnPesquisar} onClick={handleSearch}>
            Pesquisar
          </button>
        </div>
      </div>

      {/* Filtros adicionais */}
      <div className="filters-section" style={{ marginBottom: '1.5rem' }}>
        <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div className="filter-group">
            <label>Sexo:</label>
            <select
              value={sexo}
              onChange={(e) => setSexo(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Cidade:</label>
            <input
              type="text"
              placeholder="Digite a cidade..."
              value={cidade}
              onChange={(e) => setCidade(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Idade Mínima:</label>
            <input
              type="number"
              placeholder="Ex: 18"
              value={idadeMin}
              onChange={(e) => setIdadeMin(e.target.value)}
              className="filter-select"
              min="0"
              max="120"
            />
          </div>

          <div className="filter-group">
            <label>Idade Máxima:</label>
            <input
              type="number"
              placeholder="Ex: 65"
              value={idadeMax}
              onChange={(e) => setIdadeMax(e.target.value)}
              className="filter-select"
              min="0"
              max="120"
            />
          </div>

          <div className="filter-group">
            <label>Itens por página:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="filter-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          {(searchTerm || sexo || cidade || idadeMin || idadeMax) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSexo('');
                setCidade('');
                setIdadeMin('');
                setIdadeMax('');
              }}
              className="clear-filters-btn"
              style={{ alignSelf: 'end' }}
            >
              🔄 Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Resumo dos Dados Filtrados */}
      <div className="compact-stats">
        <div className="compact-stat-card blue">
          <div className="compact-stat-value">{(resumo.total || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Total Pacientes</div>
        </div>
        <div className="compact-stat-card cyan">
          <div className="compact-stat-value">{(resumo.masculino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Masculino</div>
        </div>
        <div className="compact-stat-card pink">
          <div className="compact-stat-value">{(resumo.feminino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Feminino</div>
        </div>
        <div className="compact-stat-card purple">
          <div className="compact-stat-value">{resumo.idadeMedia || 0} anos</div>
          <div className="compact-stat-label">Idade Média</div>
        </div>
        <div className="compact-stat-card green">
          <div className="compact-stat-value">{(resumo.totalSolicitacoes || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Total Solicitações</div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-responsive">
          <table className="data-table compact-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('ID')} className="sortable" style={{ width: '80px' }}>
                  ID {sortField === 'ID' && (sortOrder === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('NOME')} className="sortable">
                  Paciente {sortField === 'NOME' && (sortOrder === 'ASC' ? '↑' : '↓')}
                </th>
                <th style={{ width: '200px' }}>Contato</th>
                <th onClick={() => handleSort('TOTAL_SOLICITACOES')} className="sortable" style={{ width: '120px', textAlign: 'center' }}>
                  Solicitações {sortField === 'TOTAL_SOLICITACOES' && (sortOrder === 'ASC' ? '↑' : '↓')}
                </th>
                <th style={{ width: '100px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pacientes.map((paciente) => (
                <React.Fragment key={paciente.ID}>
                  <tr
                    onClick={() => handleOpenModal(paciente)}
                    style={{ cursor: 'pointer' }}
                    className="clickable-row"
                  >
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <strong>{paciente.ID}</strong>
                    </td>
                    <td>
                      <strong style={{ fontSize: '1.05rem', color: '#1f2937' }}>{paciente.NOME}</strong>
                      <div style={{ marginTop: '4px', color: '#6b7280', fontSize: '0.9rem' }}>
                        CPF: {paciente.CPF || '-'}
                      </div>
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px', fontSize: '0.9rem' }}>
                      {paciente.EMAIL && <div>📧 {paciente.EMAIL}</div>}
                      {paciente.TELEFONE && <div>☎️ {paciente.TELEFONE}</div>}
                      {!paciente.EMAIL && !paciente.TELEFONE && '-'}
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                        {paciente.TOTAL_SOLICITACOES || 0}
                      </div>
                      {paciente.ULTIMA_SOLICITACAO && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                          Última: {formatDate(paciente.ULTIMA_SOLICITACAO)}
                        </div>
                      )}
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="view-button"
                        onClick={() => handleViewPaciente(paciente.ID)}
                      >
                        Detalhes
                      </button>
                    </td>
                  </tr>
                  <tr onClick={() => handleOpenModal(paciente)} style={{ cursor: 'pointer' }} className="clickable-row">
                    <td style={{ borderTop: 'none', paddingTop: '0', color: '#6b7280', fontSize: '0.9rem' }}>
                      {paciente.SEXO === 'M' ? '👨' : paciente.SEXO === 'F' ? '👩' : '👤'} {paciente.SEXO === 'M' ? 'Masculino' : paciente.SEXO === 'F' ? 'Feminino' : 'Não informado'} •
                      {paciente.DATANASCIMENTO && ` Nasc: ${formatDate(paciente.DATANASCIMENTO)}`}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
      />

      {/* Modal de Resumo do Paciente */}
      {modalOpen && selectedPaciente && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Header do Modal */}
            <div className="modal-header">
              <div>
                <h2>{selectedPaciente.NOME}</h2>
                <div className="modal-header-sub">ID: {selectedPaciente.ID}</div>
              </div>
              <button onClick={handleCloseModal} className="modal-close-btn">
                ✕
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="modal-body">
              {/* Informações Principais */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '0.75rem',
                  marginBottom: '1.5rem'
                }}
              >
                <div className="modal-info-card">
                  <div className="modal-info-label">CPF</div>
                  <div className="modal-info-value">
                    {selectedPaciente.CPF || 'Não informado'}
                  </div>
                </div>

                <div className="modal-info-card">
                  <div className="modal-info-label">Data de Nascimento</div>
                  <div className="modal-info-value">
                    {formatDate(selectedPaciente.DATANASCIMENTO)}
                    {calcularIdade(selectedPaciente.DATANASCIMENTO) && (
                      <span style={{ fontSize: '0.85rem', color: '#888', marginLeft: '0.5rem', fontWeight: '400' }}>
                        ({calcularIdade(selectedPaciente.DATANASCIMENTO)} anos)
                      </span>
                    )}
                  </div>
                </div>

                <div className="modal-info-card">
                  <div className="modal-info-label">Sexo</div>
                  <div className="modal-info-value">
                    {selectedPaciente.SEXO === 'M' || selectedPaciente.SEXO === 'Masculino' ? 'Masculino' : selectedPaciente.SEXO === 'F' || selectedPaciente.SEXO === 'Feminino' ? 'Feminino' : 'Não informado'}
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '0.75rem',
                  marginBottom: '1.5rem'
                }}
              >
                {selectedPaciente.TELEFONE && (
                  <div className="modal-info-card">
                    <div className="modal-info-label">Telefone</div>
                    <div className="modal-info-value">{selectedPaciente.TELEFONE}</div>
                  </div>
                )}

                {selectedPaciente.EMAIL && (
                  <div className="modal-info-card">
                    <div className="modal-info-label">Email</div>
                    <div className="modal-info-value" style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>
                      {selectedPaciente.EMAIL}
                    </div>
                  </div>
                )}
              </div>

              {/* Estatísticas */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '0.75rem',
                  marginBottom: '1.5rem'
                }}
              >
                <div className="modal-stat-card">
                  <div className="modal-stat-label">Total de Solicitações</div>
                  <div className="modal-stat-value">{selectedPaciente.TOTAL_SOLICITACOES || 0}</div>
                </div>

                {selectedPaciente.ULTIMA_SOLICITACAO && (
                  <div className="modal-stat-card">
                    <div className="modal-stat-label">Última Solicitação</div>
                    <div className="modal-stat-value" style={{ fontSize: '1.1rem' }}>
                      {formatDate(selectedPaciente.ULTIMA_SOLICITACAO)}
                    </div>
                  </div>
                )}
              </div>

              {/* Ações */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Ações
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.75rem'
                  }}
                >
                  <button
                    onClick={() => {
                      handleCloseModal();
                      navigate(`/pacientes/${selectedPaciente.ID}`);
                    }}
                    className="modal-action-btn primary"
                  >
                    Ver Detalhes Completos
                  </button>

                  <button
                    onClick={() => {
                      handleCloseModal();
                      navigate(`/atendimentos?pacienteId=${selectedPaciente.ID}`);
                    }}
                    className="modal-action-btn success"
                  >
                    Ver Atendimentos
                  </button>

                  <button
                    onClick={() => {
                      handleCloseModal();
                      navigate(`/solicitacoes?pacienteId=${selectedPaciente.ID}`);
                    }}
                    className="modal-action-btn"
                  >
                    Ver Solicitações
                  </button>

                  <button
                    onClick={handleCloseModal}
                    className="modal-action-btn"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pacientes;
